import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar,
  Users,
  ChevronLeft,
  Share2,
  User,
  Mail,
  Loader2,
  DollarSign,
  CalendarDays,
  Clock3,
  Building,
  GraduationCap,
  AlertCircle,
  LinkIcon,
  X,
  MapPinned,
  UserCheck,
  Video,
  Ticket,
  PartyPopper,
  Info,
  BookOpen,
  Edit,
  Trash2,
  Check,
} from "lucide-react";

const ApprovalSection = ({ event, onApprove, onReject }) => {
  if (
    event.organizer.type === "College" ||
    event.status !== "pending_approval"
  ) {
    return null;
  }

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-6">
      <div className="flex items-start space-x-3">
        <div className="flex-1">
          <h3 className="text-lg font-medium text-yellow-900">
            This event requires your approval
          </h3>
          <p className="mt-1 text-sm text-yellow-700">
            Review the event details and approve or reject this event.
          </p>

          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
            <div className="flex-1">
              <label className="text-sm font-medium text-yellow-900">
                Approval Comment
              </label>
              <textarea
                rows={2}
                className="mt-1 block w-full rounded-md border-yellow-300 shadow-sm 
                  focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                placeholder="Add a comment (optional)"
              />
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onApprove}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md 
                  shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <Check className="h-4 w-4 mr-2" />
                Approve
              </button>
              <button
                onClick={onReject}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md 
                  shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <X className="h-4 w-4 mr-2" />
                Reject
              </button>
            </div>
          </div>
        </div>
      </div>

      {event.approvalDetails?.comment && (
        <div className="mt-4 pt-4 border-t border-yellow-200">
          <p className="text-sm text-yellow-700">
            <strong>Previous Comment:</strong> {event.approvalDetails.comment}
          </p>
        </div>
      )}
    </div>
  );
};

const EventMetric = ({ icon: Icon, label, value, className }) => (
  <div className={`flex items-center space-x-2 ${className}`}>
    <Icon className="h-5 w-5 text-gray-400" />
    <div>
      <div className="text-sm font-medium text-gray-900">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  </div>
);

const CollegeEvenDetails = () => {
  // Move all useState declarations to the top
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentAlumni, setCurrentAlumni] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [approvalComment, setApprovalComment] = useState("");

  // Add a success notification state
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });

  // Move useEffect to be right after all useState declarations
  useEffect(() => {
    const initialize = async () => {
      await Promise.all([fetchEventDetails()]);
    };
    initialize();
  }, [id]);

  // Define all the helper functions after hooks

  const fetchEventDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/events/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch event details");
      }

      const data = await response.json();
      setEvent(data);
    } catch (err) {
      console.error("Error in fetchEventDetails:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/events/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to delete event");
      navigate("/college/events");
    } catch (err) {
      setError(err.message);
    }
  };

  const getEventData = () => {
    console.log("getevent called...");
  };

  const handleApprove = async (comment) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/college/events/${id}/approve`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ comment }),
        }
      );

      if (!response.ok) throw new Error("Failed to approve event");

      await fetchEventDetails();
      setNotification({
        show: true,
        message: "Event approved successfully",
        type: "success",
      });
      setTimeout(
        () => setNotification({ show: false, message: "", type: "" }),
        3000
      );
    } catch (err) {
      setNotification({
        show: true,
        message: "Failed to approve event",
        type: "error",
      });
      console.error("Error approving event:", err);
    }
  };

  // Update your handleReject function similarly:
  const handleReject = async (comment) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/college/events/${id}/reject`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ comment }),
        }
      );

      if (!response.ok) throw new Error("Failed to reject event");

      await fetchEventDetails();
      setNotification({
        show: true,
        message: "Event rejected successfully",
        type: "success",
      });
      setTimeout(
        () => setNotification({ show: false, message: "", type: "" }),
        3000
      );
    } catch (err) {
      setNotification({
        show: true,
        message: "Failed to reject event",
        type: "error",
      });
      console.error("Error rejecting event:", err);
    }
  };
  const handleRegister = async () => {
    try {
      setIsRegistering(true); // Now this is properly defined
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/events/${id}/register`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to register for event");

      await fetchEventDetails();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsRegistering(false); // Now this is properly defined
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: event.title,
        text: `Join us for ${event.title}`,
        url: window.location.href,
      });
    } else {
      setShowShareMenu(!showShareMenu);
    }
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    if (startDate.toDateString() === endDate.toDateString()) {
      return {
        date: startDate.toLocaleDateString("en-US", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        }),
        time: `${startDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        })} - ${endDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        })}`,
      };
    }

    return {
      date: `${startDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      })} - ${endDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })}`,
      time: "Multiple Days",
    };
  };

  const getDaysUntilEvent = () => {
    const now = new Date();
    const start = new Date(event.schedule.startDate);
    const diffTime = start - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getEventStatus = () => {
    const now = new Date();
    const start = new Date(event.schedule.startDate);
    const end = new Date(event.schedule.endDate);

    if (now < start)
      return {
        text: "Upcoming",
        color: "bg-blue-100 text-blue-800",
      };
    if (now > end)
      return {
        text: "Completed",
        color: "bg-gray-100 text-gray-800",
      };
    return {
      text: "In Progress",
      color: "bg-green-100 text-green-800",
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <p className="text-gray-600 animate-pulse">
            Loading event details...
          </p>
        </div>
      </div>
    );
  }

  const dateRange = formatDateRange(
    event.schedule.startDate,
    event.schedule.endDate
  );
  const daysUntil = getDaysUntilEvent();
  const status = getEventStatus();
  const isOrganizer = event?.organizer?.organizedBy?._id === currentAlumni?._id;
  const isRegistered = event?.registrations?.some(
    (reg) => reg?.alumni?._id === currentAlumni?._id
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error || "Event not found"}</p>
          <button
            onClick={() => navigate("/college/events")}
            className="text-blue-500 hover:text-blue-700"
          >
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  const isUpcoming = new Date(event.schedule.startDate) > new Date();
  const canRegister =
    isUpcoming &&
    event.status === "published" &&
    !isRegistered &&
    (!event.capacity.max || event.capacity.remaining > 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Image */}
      <div className="relative h-96 bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 overflow-hidden">
        {event.images?.[0] && (
          <>
            <div className="absolute inset-0">
              <img
                src={event.images[0]}
                alt={event.title}
                className="w-full h-full object-cover opacity-20"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent" />
            </div>
          </>
        )}

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <button
            onClick={() => navigate("/college/events")}
            className="absolute top-8 text-white hover:text-gray-200 flex items-center"
          >
            <ChevronLeft className="h-5 w-5 mr-1" />
            Back to Events
          </button>
          <div className="absolute top-8 right-4 flex items-center space-x-4">
            {isOrganizer && (
              <button
                onClick={() => navigate(`/events/edit/${id}`)}
                className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 backdrop-blur-lg 
        rounded-lg text-white hover:bg-opacity-30 transition-colors"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Event
              </button>
            )}
            <button
              onClick={handleShare}
              className="p-2 text-white hover:bg-white/20 rounded-full transition-colors"
            >
              <Share2 className="h-5 w-5" />
            </button>
          </div>

          <div className="pt-32 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${status.color}`}
              >
                {status.text}
              </span>
              <h1 className="text-4xl font-bold text-white sm:text-5xl">
                {event.title}
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Organized by {event.organizer.organizedBy.name}
              </p>
              <div className="mt-6 md:mt-0 md:ml-8">
                <button
                  onClick={handleDelete}
                  className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-600 text-gray-50 rounded-xl transition-colors"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Event
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {event.status === "pending_approval" &&
        event.organizer.type === "Alumni" && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 mb-8 relative z-10">
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl shadow-lg p-6">
              <div className="flex items-start space-x-4">
                <AlertCircle className="h-6 w-6 text-yellow-500 mt-1" />
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-yellow-900">
                    This event requires your approval
                  </h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    This event was created by an alumni and needs your review
                    before it can be published.
                  </p>

                  <div className="mt-4">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-yellow-900">
                        Approval Comment
                      </label>
                      <textarea
                        rows={2}
                        className="mt-1 block w-full rounded-md border-yellow-300 shadow-sm 
                  focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                        placeholder="Add a comment (optional)"
                        value={approvalComment}
                        onChange={(e) => setApprovalComment(e.target.value)}
                      />
                    </div>

                    <div className="flex space-x-4">
                      <button
                        onClick={() => handleApprove(approvalComment)}
                        className="inline-flex items-center px-4 py-2 border border-transparent 
                  rounded-md shadow-sm text-sm font-medium text-white bg-green-600 
                  hover:bg-green-700 focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Approve Event
                      </button>

                      <button
                        onClick={() => handleReject(approvalComment)}
                        className="inline-flex items-center px-4 py-2 border border-transparent 
                  rounded-md shadow-sm text-sm font-medium text-white bg-red-600 
                  hover:bg-red-700 focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reject Event
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Event Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Info Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <EventMetric
                  icon={CalendarDays}
                  label="Date"
                  value={dateRange.date}
                />
                <EventMetric
                  icon={Clock3}
                  label="Time"
                  value={dateRange.time}
                />
                <EventMetric
                  icon={Users}
                  label="Capacity"
                  value={`${event.registrations.length}/${
                    event.capacity.max || "∞"
                  }`}
                />
                <EventMetric
                  icon={DollarSign}
                  label="Registration Fee"
                  value={
                    event.registration.fee.amount > 0
                      ? `${event.registration.fee.amount} ${event.registration.fee.currency}`
                      : "Free"
                  }
                />
              </div>
            </motion.div>

            {/* Event Description */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                About This Event
              </h2>
              <div className="prose max-w-none text-gray-600">
                {event.description}
              </div>
            </div>

            {/* Agenda Section */}
            {event.agenda?.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  Event Schedule
                </h2>
                <div className="space-y-6">
                  {event.agenda.map((item, index) => (
                    <div key={index} className="relative pl-8 pb-6 last:pb-0">
                      <div className="absolute left-0 top-0 h-full w-px bg-blue-200">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-4 w-4 rounded-full border-2 border-blue-500 bg-white" />
                      </div>
                      <div>
                        <time className="text-sm font-medium text-blue-600">
                          {new Date(item.time).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </time>
                        <h3 className="mt-1 text-lg font-medium text-gray-900">
                          {item.title}
                        </h3>
                        {item.description && (
                          <p className="mt-1 text-gray-600">
                            {item.description}
                          </p>
                        )}
                        {item.speaker && (
                          <div className="mt-2 flex items-center text-sm text-gray-500">
                            <User className="h-4 w-4 mr-1.5" />
                            <span>Speaker: {item.speaker}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Location/Venue Details */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {event.venue.isVirtual
                  ? "Virtual Event Details"
                  : "Event Venue"}
              </h2>

              {event.venue.isVirtual ? (
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Video className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Virtual Event
                    </h3>
                    {event.venue.meetingLink && (
                      <a
                        href={event.venue.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center text-blue-600 hover:text-blue-800"
                      >
                        <LinkIcon className="h-4 w-4 mr-1.5" />
                        Join Meeting
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <MapPinned className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {event.venue.name}
                      </h3>
                      <p className="text-gray-600">{event.venue.address}</p>
                      <p className="text-gray-600">
                        {event.venue.city}, {event.venue.state}{" "}
                        {event.venue.pincode}
                      </p>
                    </div>
                  </div>

                  {/* Add a map here if you have coordinates */}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Registration Card */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Registration Details
              </h3>

              <div className="space-y-4">
                {/* Registration Status */}
                <div className="flex justify-between items-center pb-4 border-b">
                  <span className="text-gray-600">Status</span>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                    ${
                      event.registration.isRequired
                        ? "bg-blue-100 text-blue-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {event.registration.isRequired
                      ? "Registration Required"
                      : "Open Entry"}
                  </span>
                </div>

                {/* Deadline */}
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Registration Closes</span>
                  <span className="font-medium text-gray-900">
                    {new Date(event.registration.deadline).toLocaleDateString()}
                  </span>
                </div>

                {/* Available Spots */}
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Available Spots</span>
                  <span className="font-medium text-gray-900">
                    {event.capacity.remaining}/{event.capacity.max || "∞"}
                  </span>
                </div>
              </div>

              {/* Registration Button */}
              {/* <button
                className="mt-6 w-full inline-flex items-center justify-center px-4 py-2 border border-transparent 
                  rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Ticket className="h-4 w-4 mr-2" />
                Register Now
              </button> */}
            </div>

            {/* Event Highlights */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Event Highlights
              </h3>
              {/* Continue from Event Highlights section */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3 text-gray-600">
                  <PartyPopper className="h-5 w-5 text-yellow-500" />
                  <span>Hands-on workshop experience</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-600">
                  <BookOpen className="h-5 w-5 text-blue-500" />
                  <span>Expert-led sessions</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-600">
                  <UserCheck className="h-5 w-5 text-green-500" />
                  <span>Networking opportunities</span>
                </div>
              </div>
            </div>
          </div>

          {/* Timer Card (for upcoming events) */}
          {daysUntil > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Event Starts In
              </h3>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-2xl font-bold text-blue-600">
                    {Math.floor(daysUntil)}
                  </div>
                  <div className="text-xs text-gray-500">Days</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-2xl font-bold text-blue-600">
                    {Math.floor((daysUntil % 1) * 24)}
                  </div>
                  <div className="text-xs text-gray-500">Hours</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-2xl font-bold text-blue-600">
                    {Math.floor((((daysUntil % 1) * 24) % 1) * 60)}
                  </div>
                  <div className="text-xs text-gray-500">Minutes</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-2xl font-bold text-blue-600">
                    {Math.floor((((((daysUntil % 1) * 24) % 1) * 60) % 1) * 60)}
                  </div>
                  <div className="text-xs text-gray-500">Seconds</div>
                </div>
              </div>
            </div>
          )}

          {/* Organizer Info */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Organized By
            </h3>
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                {event.organizer.type === "College" ? (
                  <Building className="h-6 w-6 text-gray-600" />
                ) : (
                  <GraduationCap className="h-6 w-6 text-gray-600" />
                )}
              </div>
              <div>
                <h4 className="text-base font-medium text-gray-900">
                  {event.organizer.organizedBy.name}
                </h4>
                <p className="text-sm text-gray-500">
                  {event.organizer.type === "College"
                    ? "Educational Institution"
                    : "Alumni Association"}
                </p>
              </div>
            </div>
          </div>

          {/* Share Card */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Share Event</h3>
              <button
                onClick={handleShare}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <Share2 className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() =>
                  window.open(
                    `mailto:?subject=${encodeURIComponent(
                      event.title
                    )}&body=${encodeURIComponent(window.location.href)}`
                  )
                }
                className="flex-1 flex items-center justify-center px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                <Mail className="h-4 w-4 mr-2" />
                Email
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  // Add a toast notification here
                }}
                className="flex-1 flex items-center justify-center px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                Copy Link
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Images Gallery */}
      {event.images?.length > 1 && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Event Gallery
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {event.images.map((image, index) => (
              <div
                key={index}
                className="relative aspect-video rounded-lg overflow-hidden"
              >
                <img
                  src={image}
                  alt={`${event.title} - Image ${index + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Links & Info */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gray-50 rounded-xl p-6">
          <div className="flex items-center space-x-3 text-gray-600">
            <Info className="h-5 w-5" />
            <span>Need assistance? Contact the organizer for support.</span>
          </div>
        </div>
        <div className="bg-gray-50 rounded-xl p-6">
          <div className="flex items-center space-x-3 text-gray-600">
            <AlertCircle className="h-5 w-5" />
            <span>Review event guidelines before attending.</span>
          </div>
        </div>
        <div className="bg-gray-50 rounded-xl p-6">
          <div className="flex items-center space-x-3 text-gray-600">
            <Calendar className="h-5 w-5" />
            <span>Add to your calendar for reminders.</span>
          </div>
        </div>
      </div>

      {/* Share Menu Modal */}
      {showShareMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full mx-4"
          >
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Share Event
            </h3>
            <div className="space-y-4">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  setShowShareMenu(false);
                }}
                className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                Copy Link
              </button>
              <button
                onClick={() => {
                  window.open(
                    `mailto:?subject=${event.title}&body=Check out this event: ${window.location.href}`
                  );
                  setShowShareMenu(false);
                }}
                className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Mail className="h-4 w-4 mr-2" />
                Share via Email
              </button>
            </div>
            <button
              onClick={() => setShowShareMenu(false)}
              className="mt-4 w-full px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Cancel
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CollegeEvenDetails;
